<template>
  <div>
    <b-modal
      :id="modalId"
      v-model="isVisible"
      modal-class="custom-modal-width"
      body-class="compteClair"
      header-class="compteClair"
      footer-class="compteClair"
      centered
      no-fade
      no-close-on-backdrop
      no-close-on-esc
      @hidden="handleClose"
    >
      <!-- Header personnalisé -->
      <template #modal-header="{ close }">
        <div class="w-100 d-flex justify-content-between align-items-center">
          <h2
            class="titre mb-0 pt-1"
            :class="{
              'service-unavailable-title': mode === 'service-unavailable',
              h4: mode !== 'service-unavailable',
            }"
          >
            {{ modalTitle }}
          </h2>
          <button type="button" class="close" aria-label="Fermer" @click="close()">
            <svg-icon icon-name="icone-fermer-x" className="icone-fermer-modale" />
          </button>
        </div>
      </template>

      <!-- Contenu selon le mode -->
      <template v-if="mode === 'validation'">
        <!-- Boutons radio pour choisir l'adresse -->
        <div class="address-choice-container">
          <!-- Adresse suggérée -->
          <div class="address-option">
            <label class="address-label">{{ suggestedAddressTitle }}</label>
            <BoutonRadioPersonnalise
              id="suggestedAddressOption"
              :model-value="selectedAddressType"
              value="suggested"
              name="addressChoice"
              :is-suggested="true"
              @update:modelValue="handleAddressTypeChange"
            >
              <template #content>
                <!-- Adresse suggérée unique -->
                <div v-if="suggestedAddress" class="mb-0 address-text address-multiline">
                  <span class="address-text address-multiline">
                    {{ formatAddressWithApartment(suggestedAddress) }}
                  </span>
                </div>

                <!-- Aucune adresse suggérée -->
                <div v-else class="mb-0 address-text text-muted font-italic">
                  {{ labels.noSuggestedAddress || 'Aucune adresse suggérée' }}
                </div>
              </template>
            </BoutonRadioPersonnalise>
          </div>

          <!-- Adresse saisie -->
          <div class="address-option">
            <label class="address-label">{{ labels.enteredAddress || 'Adresse saisie' }}</label>
            <BoutonRadioPersonnalise
              id="enteredAddressOption"
              :model-value="selectedAddressType"
              value="entered"
              name="addressChoice"
              @update:modelValue="handleAddressTypeChange"
            >
              <template #content>
                <span class="mb-0 address-text address-multiline">
                  {{ formatAddressWithApartment(enteredAddress) }}
                </span>
              </template>
            </BoutonRadioPersonnalise>
          </div>
        </div>
      </template>
      <!-- Mode Not Validated (adresse non vérifiée) -->
      <template v-else-if="mode === 'not-validated'">
        <!-- Message d'explication -->
        <p class="mb-3">
          {{
            labels.notValidatedInfo || "Assurez-vous que votre adresse ne contient pas d'erreur."
          }}
        </p>

        <!-- Adresse saisie en gras -->
        <span class="mb-3 address-text address-multiline">
          {{ formatAddressWithApartment(enteredAddress) }}
        </span>
      </template>

      <!-- Mode Service indisponible -->
      <template v-else-if="mode === 'service-unavailable'">
        <!-- Message d'explication -->
        <p class="mb-3">
          {{ labels.serviceUnavailableMessage || 'Vérifiez-la avant de continuer.' }}
        </p>

        <!-- Adresse saisie en gras -->
        <p class="mb-3 address-text">
          <span class="address-multiline">
            {{ formatAddressWithApartment(enteredAddress) }}
          </span>
        </p>
      </template>

      <!-- Footer avec boutons -->
      <template #modal-footer>
        <div
          class="w-100 d-flex flex-column flex-sm-row"
          :class="{
            'justify-content-center': mode !== 'service-unavailable',
            'justify-content-between': mode === 'service-unavailable',
          }"
          style="gap: 1rem"
        >
          <!-- Mode validation -->
          <template v-if="mode === 'validation'">
            <b-button
              variant="secondaire"
              size="md"
              class="mb-0 order-1 order-sm-1"
              @click="handleModifyAddress"
            >
              {{ labels.btnModifyAddress || "Modifier l'adresse" }}
            </b-button>
            <b-button
              variant="primaire"
              size="md"
              class="mb-0 order-2 order-sm-2"
              @click="handleContinueWithoutValidation"
            >
              {{ labels.btnContinueAndSave || 'Continuer avec cette adresse' }}
            </b-button>
          </template>

          <!-- Mode not-validated -->
          <template v-else-if="mode === 'not-validated'">
            <b-button
              variant="primaire"
              size="md"
              class="mb-0 order-1 order-sm-1"
              @click="handleModifyAddress"
            >
              {{ labels.btnModifyAddress || "Modifier l'adresse" }}
            </b-button>
            <b-button
              variant="secondaire"
              size="md"
              class="mb-0 order-2 order-sm-2"
              @click="handleContinueWithoutValidation"
            >
              {{ labels.btnContinueAndSave || 'Continuer avec cette adresse' }}
            </b-button>
          </template>

          <!-- Mode service indisponible -->
          <template v-else-if="mode === 'service-unavailable'">
            <b-button
              variant="secondaire"
              size="md"
              class="mb-0 order-1 order-sm-1"
              @click="handleModifyAddress"
            >
              {{ labels.btnModifyAddress || "Modifier l'adresse" }}
            </b-button>
            <b-button
              variant="primaire"
              size="md"
              class="mb-0 order-2 order-sm-2"
              @click="handleContinueWithoutValidation"
            >
              {{ labels.btnContinueAndSave || 'Continuer avec cette adresse' }}
            </b-button>
          </template>
        </div>
      </template>
    </b-modal>
  </div>
</template>

<script>
import BoutonRadioPersonnalise from '@/components/formulaire/compte/BoutonRadioPersonnalise.vue';
import SvgIcon from '@/helpers/SvgIcon.vue';
import { formatAddressWithApartment } from '@/helpers/AddressHelpers';
import '@/assets/sprite/icone-fermer-x.svg';

export default {
  name: 'AdresseConfirmationModale',

  components: {
    BoutonRadioPersonnalise,
    SvgIcon,
  },

  props: {
    visible: {
      type: Boolean,
      required: true,
    },
    mode: {
      type: String,
      required: true,
      validator: (value) => ['validation', 'not-validated', 'service-unavailable'].includes(value),
    },
    labels: {
      type: Object,
      required: true,
    },
    enteredAddress: {
      type: Object,
      required: true,
    },
    suggestedAddress: {
      type: Object,
      default: null,
    },
    validationResults: {
      type: Array,
      default: () => [],
    },
    showValidationDropdown: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      isVisible: this.visible,
      selectedAddressType: 'suggested', // Par défaut, sélectionner l'adresse suggérée
      selectedDropdownIndex: '0',
      isMobile: false,
    };
  },

  computed: {
    modalId() {
      if (this.mode === 'service-unavailable') {
        return 'service-unavailable-modal';
      }
      if (this.mode === 'not-validated') {
        return 'not-validated-modal';
      }
      return 'address-confirmation-modal';
    },

    modalTitle() {
      if (this.mode === 'validation') {
        return this.labels.confirmAddress || "Oups! Nous n'avons pas pu trouvé l'adresse exacte";
      }
      if (this.mode === 'not-validated') {
        return this.labels.notValidatedTitle || "Oups! Nous n'avons pas trouvé cette adresse";
      }
      return (
        this.labels.serviceUnavailableTitle ||
        'Oups! Nous ne sommes pas en mesure de vérifier votre adresse'
      );
    },

    suggestedAddressTitle() {
      if (
        this.showValidationDropdown &&
        this.validationResults &&
        this.validationResults.length > 0
      ) {
        const count = this.validationResults.length;
        return `${count} ${count > 1 ? 'Adresses suggérées' : 'Adresse suggérée'}`;
      }
      return this.labels.suggestedAddress || 'Adresse suggérée';
    },

    dropdownSize() {
      if (!this.validationResults || this.validationResults.length === 0) {
        return 3;
      }
      return Math.min(Math.max(this.validationResults.length, 2), 3);
    },
  },

  watch: {
    visible(newVal) {
      this.isVisible = newVal;

      // Réinitialiser la sélection quand la modale s'ouvre
      if (newVal) {
        // En mode validation, sélectionner l'adresse suggérée par défaut si elle existe
        if (this.mode === 'validation' && this.suggestedAddress) {
          this.selectedAddressType = 'suggested';
        } else {
          this.selectedAddressType = 'entered';
        }

        // Émettre la sélection initiale au parent
        this.$emit('update:selectedAddress', this.selectedAddressType);
      }
    },

    validationResults: {
      handler(newResults) {
        if (newResults && newResults.length > 0) {
          this.selectedDropdownIndex = '0';
          const firstResult = newResults[0];
          this.$emit('address-select', firstResult);
        } else {
          this.selectedDropdownIndex = '';
        }
      },
      deep: true,
    },
  },

  mounted() {
    this.checkIsMobile();
    window.addEventListener('resize', this.checkIsMobile);
  },

  beforeDestroy() {
    window.removeEventListener('resize', this.checkIsMobile);
  },

  methods: {
    checkIsMobile() {
      if (typeof window !== 'undefined') {
        this.isMobile = window.innerWidth <= 768;
      }
    },

    formatAddressWithApartment(address) {
      return formatAddressWithApartment(address, this.$i18n.locale);
    },

    handleAddressTypeChange(value) {
      this.selectedAddressType = value;
      this.$emit('update:selectedAddress', value);
    },

    handleAddressSelect(event) {
      const index = event.target.value;
      this.selectedDropdownIndex = index;

      if (this.validationResults && index !== '') {
        const selectedResult = this.validationResults[parseInt(index, 10)];
        this.$emit('address-select', selectedResult);
      }
    },

    handleClose() {
      this.$emit('close');
    },

    handleContinue() {
      this.$emit('continue', this.selectedAddressType);
    },

    handleContinueWithoutValidation() {
      this.$emit('continue-without-validation', this.selectedAddressType);
    },

    handleModifyAddress() {
      this.$emit('modify-address');
    },
  },
};
</script>

<style lang="scss" scoped>
// Largeur personnalisée de la modale en mode desktop
::v-deep .custom-modal-width .modal-dialog {
  width: 500px;
  margin: 1.75rem auto; // Centrage horizontal en desktop

  @media (max-width: 768px) {
    width: calc(100% - 1rem); // Full-width avec marges de 0.5rem de chaque côté
    max-width: 100%;
    margin: 0.5rem auto; // Centrage horizontal en mobile
  }
}

// Style pour le titre en mode service-unavailable
.service-unavailable-title {
  color: #192872 !important;
  font-size: 24px !important;
  font-style: normal !important;
  font-weight: 700 !important;
}

// Styles pour le bouton de fermeture (X)
::v-deep .modal-header {
  position: relative;
  padding: 2rem 2rem 0 2rem !important;
  border-bottom: none !important;

  // Limiter la largeur du titre pour ne pas chevaucher la croix
  h2,
  h4 {
    max-width: 100%;
    padding-right: 1rem;
  }

  .close {
    position: absolute;
    top: 1rem;
    right: 1rem;
    font-size: 24px;
    font-weight: 300;
    line-height: 1;
    color: #000233;
    opacity: 0.5;
    padding: 0;
    margin: 0;
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    border: none;
    background: none;

    &:hover {
      opacity: 0.75;
    }

    &:focus {
      outline: 2px solid #192872;
      outline-offset: 2px;
      opacity: 0.75;
    }

    .icone-fermer-modale {
      width: 24px;
      height: 24px;
    }
  }
}

// Appliquer la couleur #000233 à tout le contenu de la modale (sauf les boutons)
::v-deep .modal-body {
  color: #000233;
  text-align: left !important;
  padding: 1rem 2rem 1rem 2rem !important;

  p,
  h4,
  strong,
  span,
  div:not(.btn) {
    color: #000233;
    text-align: left !important;
  }

  // S'assurer que les paragraphes ont moins d'espacement en bas
  p {
    margin-bottom: 0.5rem !important;
  }
}

// Styles pour les boutons de la modale
::v-deep .modal-footer {
  padding: 0 2rem 2rem 2rem !important;
  border-top: none !important;

  .btn {
    line-height: 1;
    font-size: 15px;
    padding: 0.5rem 1rem !important;
  }

  // S'assurer que le texte des boutons primaires garde sa couleur d'origine
  button.btn.btn-primaire,
  a.btn.btn-primaire {
    color: #a6ecff !important;

    span {
      color: #a6ecff !important;
    }
  }

  // Style personnalisé pour le bouton tertiaire - bordure de la couleur du bouton primaire
  .btn-tertiaire-custom {
    border-color: #192872 !important;
    border-width: 2px !important;
    border-radius: 50px !important;

    // Hover state
    &:hover {
      background-color: rgba(25, 40, 114, 0.05) !important;
      border-color: darken(#192872, 10%) !important;
    }

    // Focus state
    &:focus {
      box-shadow: 0 0 0 3px rgba(25, 40, 114, 0.25) !important;
      border-color: #192872 !important;
    }

    // Active state
    &:active {
      background-color: rgba(25, 40, 114, 0.1) !important;
      border-color: darken(#192872, 15%) !important;
    }
  }
}

.address-choice-container {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
  align-self: stretch;
}

.address-option {
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
}

.address-label {
  font-size: 16px;
  font-weight: 600;
  line-height: 24px;
  color: #000233;
  margin: 0;
}

.address-text {
  font-size: 16px;
  font-weight: bold;
  line-height: 24px;
  color: #000233;
}

.slot-content .address-text {
  font-weight: 100;
}

.address-multiline {
  white-space: pre-line;
}

.address-dropdown-select {
  overflow-y: auto;
  overflow-x: hidden;
  max-height: 180px;
  min-height: 107px;
  padding: 4px;
  font-size: 14px;
  line-height: 1.5;
  height: auto !important;
  border: 1px solid #ccceff;
  scrollbar-width: thin;
  scrollbar-color: #ccceff #f7fafc;

  option {
    white-space: normal;
    word-wrap: break-word;
    overflow-wrap: break-word;
    padding: 12px 10px;
    line-height: 1.5;
    min-height: 48px;
    display: block;
    cursor: pointer;
    border-bottom: 1px solid #f3f4f6;

    &:last-child {
      border-bottom: none;
    }

    &:checked {
      background-color: #dbeafe !important;
      font-weight: 600;
      color: #1e40af;
    }

    &:hover,
    &:focus {
      background-color: #f0f9ff;
    }
  }
}

.gap-3 {
  gap: 1rem;
}

.text-sm {
  font-size: 0.875rem;
}

@media (max-width: 768px) {
  .address-dropdown-select {
    max-height: 200px;
    min-height: 180px;

    option {
      padding: 14px 10px;
      line-height: 1.4;
      min-height: 60px;
    }
  }
}
</style>
